import React, { createContext, useContext, useEffect, useState } from "react"

type Language = "en" | "sw"

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Translations
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Common
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.new": "New",
    "common.search": "Search",
    "common.loading": "Loading",
    "common.error": "Error",
    "common.success": "Success",
    "common.close": "Close",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.submit": "Submit",
    "common.actions": "Actions",
    "common.status": "Status",
    "common.active": "Active",
    "common.inactive": "Inactive",
    "common.yes": "Yes",
    "common.no": "No",
    "common.confirm": "Confirm",
    "common.upload": "Upload",
    "common.select": "Select",
    "common.required": "Required",
    
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.products": "Products",
    "nav.news": "News & Updates",
    "nav.investors": "Investors",
    "nav.opportunities": "Opportunities",
    "nav.team": "Team",
    "nav.settings": "Settings",
    "nav.profile": "Profile",
    "nav.logout": "Logout",
    "nav.login": "Login",
    
    // Sidebar
    "sidebar.productsCarousel": "Products Carousel",
    "sidebar.annualGeneralMeeting": "Annual General Meeting",
    "sidebar.financialReports": "Financial Reports",
    "sidebar.reports": "Reports",
    "sidebar.rightsIssue": "Rights Issue",
    "sidebar.marketDisclosures": "Market Disclosures",
    "sidebar.annualReports": "Annual Reports",
    "sidebar.tariffGuide": "Tariff Guide",
    "sidebar.shareholdingStructure": "Shareholding Structure",
    "sidebar.sharePrice": "Share Price",
    "sidebar.investorRelationsContact": "Investor Relations Contact",
    "sidebar.jobVacancies": "Job Vacancies",
    "sidebar.teamMembers": "Team Members",
    "sidebar.userManagement": "User Management",
    "sidebar.homeCarousel": "Home Carousel",
    "sidebar.menuManagement": "Menu Management",
    "sidebar.helpSupport": "Help & Support",
    
    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.contentOverview": "Content Overview",
    "dashboard.teamManagement": "Team & Management",
    "dashboard.contentManagement": "Content Management",
    "dashboard.categoriesSettings": "Categories & Settings",
    "dashboard.leadershipTeam": "Leadership and team statistics",
    "dashboard.websiteContent": "Website content and navigation",
    "dashboard.organization": "Organization and configuration",
    
    // Settings
    "settings.title": "Settings",
    "settings.description": "Manage your dashboard preferences and configuration",
    "settings.appearance": "Appearance",
    "settings.customizeLook": "Customize the look and feel of your dashboard",
    "settings.darkMode": "Dark Mode",
    "settings.switchDarkTheme": "Switch to dark theme",
    "settings.language": "Language",
    "settings.generalPreferences": "General Preferences",
    "settings.configureBehavior": "Configure default dashboard behavior",
    "settings.itemsPerPage": "Items Per Page",
    "settings.sessionTimeout": "Session Timeout (minutes)",
    "settings.notifications": "Notifications",
    "settings.manageNotifications": "Manage notification preferences",
    "settings.emailNotifications": "Email Notifications",
    "settings.receiveEmailAlerts": "Receive email alerts for important events",
    "settings.browserNotifications": "Browser Notifications",
    "settings.showBrowserNotifications": "Show browser notifications",
    "settings.security": "Security",
    "settings.configureSecurity": "Configure security and access settings",
    "settings.twoFactorAuth": "Two-Factor Authentication",
    "settings.addExtraSecurity": "Add an extra layer of security",
    "settings.passwordRequirements": "Password Requirements",
    "settings.minCharacters": "Minimum 8 characters",
    "settings.uppercaseLetter": "At least one uppercase letter",
    "settings.oneNumber": "At least one number",
    "settings.fileUpload": "File Upload",
    "settings.configureFileUpload": "Configure file upload limits and types",
    "settings.maxFileSize": "Maximum File Size (MB)",
    "settings.allowedFileTypes": "Allowed File Types",
    "settings.images": "Images: JPG, PNG, GIF, WebP",
    "settings.documents": "Documents: PDF, DOC, DOCX",
    "settings.apiConfig": "API Configuration",
    "settings.backendApi": "Backend API server settings",
    "settings.apiBaseUrl": "API Base URL",
    "settings.dataManagement": "Data Management",
    "settings.exportImport": "Export, import, and manage data",
    "settings.exportData": "Export Data",
    "settings.importData": "Import Data",
    "settings.clearCache": "Clear Cache",
    "settings.clearCacheDesc": "This will clear all cached data including your session. You will need to log in again.",
    "settings.systemInfo": "System Information",
    "settings.versionDetails": "Dashboard version and system details",
    "settings.version": "Version",
    "settings.userRole": "User Role",
    "settings.lastUpdated": "Last Updated",
    "settings.advanced": "Advanced Settings",
    "settings.advancedOptions": "Advanced configuration options",
    "settings.maintenanceMode": "Maintenance Mode",
    "settings.putInMaintenance": "Put dashboard in maintenance mode",
    "settings.debugMode": "Debug Mode",
    "settings.enableDebugLogging": "Enable detailed error logging",
    "settings.saveChanges": "Save Changes",
    "settings.saved": "Settings saved",
    "settings.savedDesc": "Your preferences have been saved successfully.",
    
    // Login
    "login.title": "Login",
    "login.enterCredentials": "Enter your credentials to access your account",
    "login.username": "Username",
    "login.password": "Password",
    "login.enterUsername": "Enter your username",
    "login.enterPassword": "Enter your password",
    "login.signIn": "Sign in",
    "login.signingIn": "Signing in...",
    "login.failed": "Login Failed",
    "login.error": "Login Error",
    "login.successful": "Login Successful",
    "login.welcomeBack": "Welcome back!",
    
    // Profile
    "profile.title": "Profile",
    "profile.personalInfo": "Personal Information",
    "profile.changePhoto": "Change Photo",
    "profile.saveChanges": "Save Changes",
    "profile.updated": "Profile updated",
    "profile.updatedDesc": "Your changes have been saved.",
    "profile.updateFailed": "Update failed",
    
    // Common table actions
    "table.noData": "No data available",
    "table.loading": "Loading...",
    "table.addNew": "Add New",
    "table.edit": "Edit",
    "table.delete": "Delete",
    "table.view": "View",
    "table.actions": "Actions",
    "table.confirmDelete": "Are you sure you want to delete this item?",
    "table.deleted": "Deleted",
    "table.deleteSuccess": "Item deleted successfully",
    "table.deleteError": "Failed to delete item",
    "table.showing": "Showing",
    "table.of": "of",
    "table.results": "results",
    
    // Carousel
    "carousel.title": "Carousel Items",
    "carousel.manage": "Manage all slides in your homepage carousel.",
    "carousel.addNew": "Add New Carousel",
    "carousel.noItems": "No carousel items yet.",
    "carousel.createFirst": "Click \"Add New Carousel\" to create your first slide.",
    "carousel.addNewSlide": "Add New Carousel Slide",
    "carousel.createSlide": "Create a new slide with background image, title, description, and call-to-action.",
    "carousel.editSlide": "Edit Carousel Slide",
    "carousel.updateSlide": "Update the carousel slide with background image, title, description, and call-to-action.",
    "carousel.backgroundImage": "Background Image",
    "carousel.chooseImage": "Choose Image",
    "carousel.titleLabel": "Title",
    "carousel.description": "Description",
    "carousel.buttonText": "Button Text",
    "carousel.buttonLink": "Button Link",
    "carousel.loading": "Loading carousel item...",
    "carousel.deleted": "Carousel slide removed successfully.",
    "carousel.deleteError": "Failed to delete carousel item",
    "carousel.saved": "Carousel slide saved successfully.",
    "carousel.saveError": "Failed to save carousel slide",
    
    // Products
    "products.title": "Products",
    "products.manage": "Manage banking products and services",
    "products.addNew": "Add New Product",
    "products.noProducts": "No products yet.",
    "products.createFirst": "Click \"Add New Product\" to create your first product.",
    "products.addNewProduct": "Add New Product",
    "products.editProduct": "Edit Product",
    "products.createProduct": "Create a new banking product or service.",
    "products.updateProduct": "Update the banking product or service.",
    "products.name": "Product Name",
    "products.description": "Product Description",
    "products.image": "Product Image",
    "products.link": "Product Link",
    "products.loading": "Loading product...",
    "products.deleted": "Product deleted successfully.",
    "products.deleteError": "Failed to delete product",
    "products.saved": "Product saved successfully.",
    "products.saveError": "Failed to save product",
    
    // News
    "news.title": "News & Updates",
    "news.manage": "Manage news articles and updates",
    "news.addNew": "Add New News",
    "news.noNews": "No news items yet.",
    "news.createFirst": "Click \"Add New News\" to create your first news item.",
    "news.addNewArticle": "Add New News Article",
    "news.editArticle": "Edit News Article",
    "news.createArticle": "Create a new news article or update.",
    "news.updateArticle": "Update the news article or update.",
    "news.titleLabel": "Title",
    "news.content": "Content",
    "news.image": "Featured Image",
    "news.category": "Category",
    "news.published": "Published",
    "news.loading": "Loading news article...",
    "news.deleted": "News article deleted successfully.",
    "news.deleteError": "Failed to delete news article",
    "news.saved": "News article saved successfully.",
    "news.saveError": "Failed to save news article",
    
    // Investors
    "investors.title": "Investor News",
    "investors.manage": "Manage investor-related news",
    "investors.categories": "Investor Categories",
    "investors.manageCategories": "Manage investor categories and documents",
    "investors.addNew": "Add New Investor News",
    "investors.editNews": "Edit Investor News",
    "investors.createNews": "Create a new investor news article.",
    "investors.updateNews": "Update the investor news article.",
    "investors.noNews": "No investor news yet.",
    "investors.createFirst": "Click \"Add New Investor News\" to create your first article.",
    "investors.loading": "Loading investor news...",
    "investors.deleted": "Investor news deleted successfully.",
    "investors.deleteError": "Failed to delete investor news",
    "investors.saved": "Investor news saved successfully.",
    "investors.saveError": "Failed to save investor news",
    "investors.categoryName": "Category Name",
    "investors.addCategory": "Add Investor Category",
    "investors.editCategory": "Edit Investor Category",
    "investors.reportType": "Report Type",
    "investors.financialReport": "Financial Report",
    "investors.annualReport": "Annual Report",
    "investors.marketDisclosure": "Market Disclosure",
    "investors.rightsIssue": "Rights Issue",
    
    // Opportunities
    "opportunities.title": "Opportunities",
    "opportunities.manage": "Manage job opportunities and vacancies",
    "opportunities.addNew": "Add New Opportunity",
    "opportunities.addNewOpportunity": "Add New Opportunity",
    "opportunities.editOpportunity": "Edit Opportunity",
    "opportunities.titleLabel": "Title",
    "opportunities.description": "Description",
    "opportunities.requirements": "Requirements",
    "opportunities.deadline": "Application Deadline",
    "opportunities.loading": "Loading opportunity...",
    "opportunities.deleted": "Opportunity deleted successfully.",
    "opportunities.deleteError": "Failed to delete opportunity",
    "opportunities.saved": "Opportunity saved successfully.",
    "opportunities.saveError": "Failed to save opportunity",
    
    // Team
    "team.boardMembers": "Board of Directors",
    "team.manageBoard": "Manage board members",
    "team.management": "Management",
    "team.manageManagement": "Manage management team members",
    "team.members": "Team Members",
    "team.manageMembers": "Manage team members",
    "team.addBoardMember": "Add Board Member",
    "team.editBoardMember": "Edit Board Member",
    "team.addManagement": "Add Management Member",
    "team.editManagement": "Edit Management Member",
    "team.name": "Name",
    "team.position": "Position",
    "team.bio": "Biography",
    "team.photo": "Photo",
    "team.loading": "Loading member...",
    "team.deleted": "Member deleted successfully.",
    "team.deleteError": "Failed to delete member",
    "team.saved": "Member saved successfully.",
    "team.saveError": "Failed to save member",
    "team.noMembers": "No members yet.",
    "team.createFirst": "Click \"Add New\" to create your first member.",
    
    // Menu
    "menu.items": "Menu Items",
    "menu.manageItems": "Manage navigation menu items",
    "menu.categories": "Menu Categories",
    "menu.manageCategories": "Manage menu categories",
    "menu.addItem": "Add Menu Item",
    "menu.editItem": "Edit Menu Item",
    "menu.addCategory": "Add Menu Category",
    "menu.editCategory": "Edit Menu Category",
    "menu.itemName": "Item Name",
    "menu.itemLink": "Item Link",
    "menu.categoryName": "Category Name",
    "menu.loading": "Loading menu item...",
    "menu.deleted": "Menu item deleted successfully.",
    "menu.deleteError": "Failed to delete menu item",
    "menu.saved": "Menu item saved successfully.",
    "menu.saveError": "Failed to save menu item",
    
    // Header
    "header.updates": "Header Updates",
    "header.manage": "Manage header announcements",
    "header.addUpdate": "Add Header Update",
    "header.editUpdate": "Edit Header Update",
    "header.title": "Title",
    "header.message": "Message",
    "header.isActive": "Active",
    "header.loading": "Loading header update...",
    "header.deleted": "Header update deleted successfully.",
    "header.deleteError": "Failed to delete header update",
    "header.saved": "Header update saved successfully.",
    "header.saveError": "Failed to save header update",
    
    // Wakala
    "wakala.title": "Wakala Locations",
    "wakala.manage": "Manage Mwalimu Wakala locations",
    "wakala.addNew": "Add New Wakala",
    "wakala.addNewLocation": "Add New Wakala Location",
    "wakala.editLocation": "Edit Wakala Location",
    "wakala.createLocation": "Create a new Wakala location.",
    "wakala.updateLocation": "Update the Wakala location.",
    "wakala.locationName": "Location Name",
    "wakala.address": "Address",
    "wakala.phone": "Phone",
    "wakala.email": "Email",
    "wakala.loading": "Loading Wakala location...",
    "wakala.deleted": "Wakala location deleted successfully.",
    "wakala.deleteError": "Failed to delete Wakala location",
    "wakala.saved": "Wakala location saved successfully.",
    "wakala.saveError": "Failed to save Wakala location",
    
    // FAQ
    "faq.title": "FAQs",
    "faq.manage": "Manage frequently asked questions",
    "faq.addNew": "Add New FAQ",
    "faq.noFaqs": "No FAQs found. Create your first FAQ to get started.",
    "faq.addNewFaq": "Add New FAQ",
    "faq.editFaq": "Edit FAQ",
    "faq.createFaq": "Create a new frequently asked question.",
    "faq.updateFaq": "Update the frequently asked question.",
    "faq.question": "Question",
    "faq.answer": "Answer",
    "faq.isActive": "Active",
    "faq.loading": "Loading FAQ...",
    "faq.deleted": "FAQ deleted successfully.",
    "faq.deleteError": "Failed to delete FAQ",
    "faq.saved": "FAQ saved successfully.",
    "faq.saveError": "Failed to save FAQ",
    
    // Users
    "users.title": "Users",
    "users.manage": "Manage dashboard users",
    "users.addNew": "Add New User",
    "users.addNewUser": "Add New User",
    "users.editUser": "Edit User",
    "users.username": "Username",
    "users.email": "Email",
    "users.password": "Password",
    "users.role": "Role",
    "users.loading": "Loading user...",
    "users.deleted": "User deleted successfully.",
    "users.deleteError": "Failed to delete user",
    "users.saved": "User saved successfully.",
    "users.saveError": "Failed to save user",
    
    // Session
    "session.expired": "Session Expired",
    "session.expiredDesc": "Your session has expired. Please login again.",
    "session.invalidToken": "Invalid Token",
    "session.tokenExpired": "The provided token has already expired. Please login again.",
    
    // Charts
    "charts.newsUpdates": "News & Updates",
    "charts.investorNews": "Investor News",
    "charts.opportunities": "Opportunities",
    "charts.products": "Products",
    "charts.carousels": "Carousels",
    "charts.menuItems": "Menu Items",
    "charts.trends": "Trends",
    "charts.monthlyGrowth": "Monthly growth",
    "charts.newsUpdatesTrends": "News & Updates Trends",
    "charts.newsUpdatesDesc": "Monthly growth of news and updates content.",
    "charts.investorNewsTrends": "Investor News Trends",
    "charts.investorNewsDesc": "Monthly growth of investor news content.",
    "charts.opportunitiesTrends": "Opportunities Trends",
    "charts.opportunitiesDesc": "Monthly growth of job opportunities and listings.",
    "charts.productsTrends": "Products Trends",
    "charts.productsDesc": "Monthly growth of banking products and services.",
    "charts.carouselsTrends": "Carousel Items Trends",
    "charts.carouselsDesc": "Monthly growth of carousel banner items.",
    "charts.menuItemsTrends": "Menu Items Trends",
    "charts.menuItemsDesc": "Monthly growth of dynamic menu items.",
    
    // Metrics
    "metrics.thisMonth": "this month",
  },
  sw: {
    // Common
    "common.save": "Hifadhi",
    "common.cancel": "Ghairi",
    "common.delete": "Futa",
    "common.edit": "Hariri",
    "common.add": "Ongeza",
    "common.new": "Mpya",
    "common.search": "Tafuta",
    "common.loading": "Inapakia",
    "common.error": "Hitilafu",
    "common.success": "Mafanikio",
    "common.close": "Funga",
    "common.back": "Rudi",
    "common.next": "Ifuatayo",
    "common.previous": "Iliyotangulia",
    "common.submit": "Wasilisha",
    "common.actions": "Vitendo",
    "common.status": "Hali",
    "common.active": "Inaendelea",
    "common.inactive": "Haijaendelea",
    "common.yes": "Ndiyo",
    "common.no": "Hapana",
    "common.confirm": "Thibitisha",
    "common.upload": "Pakia",
    "common.select": "Chagua",
    "common.required": "Inahitajika",
    
    // Navigation
    "nav.dashboard": "Dashibodi",
    "nav.products": "Bidhaa",
    "nav.news": "Habari na Matukio",
    "nav.investors": "Wawekezaji",
    "nav.opportunities": "Fursa",
    "nav.team": "Timu",
    "nav.settings": "Mipangilio",
    "nav.profile": "Wasifu",
    "nav.logout": "Toka",
    "nav.login": "Ingia",
    
    // Sidebar
    "sidebar.productsCarousel": "Carousel ya Bidhaa",
    "sidebar.annualGeneralMeeting": "Mkutano Mkuu wa Mwaka",
    "sidebar.financialReports": "Ripoti za Kifedha",
    "sidebar.reports": "Ripoti",
    "sidebar.rightsIssue": "Toleo la Haki",
    "sidebar.marketDisclosures": "Ufunuo wa Soko",
    "sidebar.annualReports": "Ripoti za Mwaka",
    "sidebar.tariffGuide": "Mwongozo wa Ada",
    "sidebar.shareholdingStructure": "Muundo wa Umiliki wa Hisa",
    "sidebar.sharePrice": "Bei ya Hisa",
    "sidebar.investorRelationsContact": "Mawasiliano ya Uhusiano wa Wawekezaji",
    "sidebar.jobVacancies": "Nafasi za Kazi",
    "sidebar.teamMembers": "Wajumbe wa Timu",
    "sidebar.userManagement": "Usimamizi wa Watumiaji",
    "sidebar.homeCarousel": "Carousel ya Nyumbani",
    "sidebar.menuManagement": "Usimamizi wa Menyu",
    "sidebar.helpSupport": "Msaada na Usaidizi",
    
    // Dashboard
    "dashboard.title": "Dashibodi",
    "dashboard.contentOverview": "Muhtasari wa Maudhui",
    "dashboard.teamManagement": "Timu na Usimamizi",
    "dashboard.contentManagement": "Usimamizi wa Maudhui",
    "dashboard.categoriesSettings": "Jamii na Mipangilio",
    "dashboard.leadershipTeam": "Takwimu za uongozi na timu",
    "dashboard.websiteContent": "Maudhui na urambazaji wa tovuti",
    "dashboard.organization": "Ushirikishaji na usanidi",
    
    // Settings
    "settings.title": "Mipangilio",
    "settings.description": "Simamia mapendeleo na usanidi wa dashibodi yako",
    "settings.appearance": "Mwonekano",
    "settings.customizeLook": "Badilisha mwonekano na hisia ya dashibodi yako",
    "settings.darkMode": "Hali ya Giza",
    "settings.switchDarkTheme": "Badilisha kwa mada ya giza",
    "settings.language": "Lugha",
    "settings.generalPreferences": "Mapendeleo ya Jumla",
    "settings.configureBehavior": "Sanidi tabia ya chaguo-msingi ya dashibodi",
    "settings.itemsPerPage": "Vipengele Kwa Ukurasa",
    "settings.sessionTimeout": "Mwisho wa Kikao (dakika)",
    "settings.notifications": "Arifa",
    "settings.manageNotifications": "Simamia mapendeleo ya arifa",
    "settings.emailNotifications": "Arifa za Barua Pepe",
    "settings.receiveEmailAlerts": "Pokea arifa za barua pepe kwa matukio muhimu",
    "settings.browserNotifications": "Arifa za Kivinjari",
    "settings.showBrowserNotifications": "Onyesha arifa za kivinjari",
    "settings.security": "Usalama",
    "settings.configureSecurity": "Sanidi mipangilio ya usalama na ufikiaji",
    "settings.twoFactorAuth": "Uthibitishaji wa Sababu Mbili",
    "settings.addExtraSecurity": "Ongeza safu ya ziada ya usalama",
    "settings.passwordRequirements": "Mahitaji ya Nenosiri",
    "settings.minCharacters": "Angalau herufi 8",
    "settings.uppercaseLetter": "Angalau herufi moja kubwa",
    "settings.oneNumber": "Angalau nambari moja",
    "settings.fileUpload": "Kupakia Faili",
    "settings.configureFileUpload": "Sanidi mipaka na aina za kupakia faili",
    "settings.maxFileSize": "Ukubwa wa Juu wa Faili (MB)",
    "settings.allowedFileTypes": "Aina za Faili Zinazoruhusiwa",
    "settings.images": "Picha: JPG, PNG, GIF, WebP",
    "settings.documents": "Hati: PDF, DOC, DOCX",
    "settings.apiConfig": "Usanidi wa API",
    "settings.backendApi": "Mipangilio ya seva ya API ya nyuma",
    "settings.apiBaseUrl": "URL ya Msingi ya API",
    "settings.dataManagement": "Usimamizi wa Data",
    "settings.exportImport": "Hamisha, ingiza, na simamia data",
    "settings.exportData": "Hamisha Data",
    "settings.importData": "Ingiza Data",
    "settings.clearCache": "Futa Hifadhi ya Kichwa",
    "settings.clearCacheDesc": "Hii itafuta data zote zilizohifadhiwa pamoja na kikao chako. Itabidi uingie tena.",
    "settings.systemInfo": "Taarifa za Mfumo",
    "settings.versionDetails": "Toleo la dashibodi na maelezo ya mfumo",
    "settings.version": "Toleo",
    "settings.userRole": "Jukumu la Mtumiaji",
    "settings.lastUpdated": "Imesasishwa Mwisho",
    "settings.advanced": "Mipangilio ya Hali ya Juu",
    "settings.advancedOptions": "Chaguzi za usanidi wa hali ya juu",
    "settings.maintenanceMode": "Hali ya Matengenezo",
    "settings.putInMaintenance": "Weka dashibodi katika hali ya matengenezo",
    "settings.debugMode": "Hali ya Utatuzi",
    "settings.enableDebugLogging": "Washa uandikaji wa kina wa makosa",
    "settings.saveChanges": "Hifadhi Mabadiliko",
    "settings.saved": "Mipangilio imehifadhiwa",
    "settings.savedDesc": "Mapendeleo yako yamehifadhiwa kwa mafanikio.",
    
    // Login
    "login.title": "Ingia",
    "login.enterCredentials": "Ingiza maelezo yako ya kuingia ili kufikia akaunti yako",
    "login.username": "Jina la Mtumiaji",
    "login.password": "Nenosiri",
    "login.enterUsername": "Ingiza jina lako la mtumiaji",
    "login.enterPassword": "Ingiza nenosiri lako",
    "login.signIn": "Ingia",
    "login.signingIn": "Inaingia...",
    "login.failed": "Kuingia Kumeshindwa",
    "login.error": "Hitilafu ya Kuingia",
    "login.successful": "Kuingia Kumefanikiwa",
    "login.welcomeBack": "Karibu tena!",
    
    // Profile
    "profile.title": "Wasifu",
    "profile.personalInfo": "Taarifa za Kibinafsi",
    "profile.changePhoto": "Badilisha Picha",
    "profile.saveChanges": "Hifadhi Mabadiliko",
    "profile.updated": "Wasifu umesasishwa",
    "profile.updatedDesc": "Mabadiliko yako yamehifadhiwa.",
    "profile.updateFailed": "Kusasisha kumeshindwa",
    
    // Common table actions
    "table.noData": "Hakuna data inayopatikana",
    "table.loading": "Inapakia...",
    "table.addNew": "Ongeza Mpya",
    "table.edit": "Hariri",
    "table.delete": "Futa",
    "table.view": "Angalia",
    "table.actions": "Vitendo",
    "table.confirmDelete": "Je, una uhakika unataka kufuta kipengele hiki?",
    "table.deleted": "Imefutwa",
    "table.deleteSuccess": "Kipengele kimefutwa kwa mafanikio",
    "table.deleteError": "Kufuta kimeshindwa",
    
    // Carousel
    "carousel.title": "Vipengele vya Carousel",
    "carousel.manage": "Simamia slaidi zote katika carousel ya ukurasa wa nyumbani.",
    "carousel.addNew": "Ongeza Carousel Mpya",
    "carousel.noItems": "Bado hakuna vipengele vya carousel.",
    "carousel.createFirst": "Bofya \"Ongeza Carousel Mpya\" ili uunde slaidi yako ya kwanza.",
    "carousel.addNewSlide": "Ongeza Slaidi Mpya ya Carousel",
    "carousel.createSlide": "Unda slaidi mpya na picha ya mandharinyuma, kichwa, maelezo, na wito wa kitendo.",
    "carousel.editSlide": "Hariri Slaidi ya Carousel",
    "carousel.updateSlide": "Sasisha slaidi ya carousel na picha ya mandharinyuma, kichwa, maelezo, na wito wa kitendo.",
    "carousel.backgroundImage": "Picha ya Mandharinyuma",
    "carousel.chooseImage": "Chagua Picha",
    "carousel.titleLabel": "Kichwa",
    "carousel.description": "Maelezo",
    "carousel.buttonText": "Maandishi ya Kitufe",
    "carousel.buttonLink": "Kiungo cha Kitufe",
    "carousel.loading": "Inapakia kipengele cha carousel...",
    "carousel.deleted": "Slaidi ya carousel imefutwa kwa mafanikio.",
    "carousel.deleteError": "Kufuta kipengele cha carousel kumeshindwa",
    "carousel.saved": "Slaidi ya carousel imehifadhiwa kwa mafanikio.",
    "carousel.saveError": "Kuhifadhi slaidi ya carousel kumeshindwa",
    
    // Products
    "products.title": "Bidhaa",
    "products.manage": "Simamia bidhaa na huduma za benki",
    "products.addNew": "Ongeza Bidhaa Mpya",
    "products.noProducts": "Bado hakuna bidhaa.",
    "products.createFirst": "Bofya \"Ongeza Bidhaa Mpya\" ili uunde bidhaa yako ya kwanza.",
    "products.addNewProduct": "Ongeza Bidhaa Mpya",
    "products.editProduct": "Hariri Bidhaa",
    "products.createProduct": "Unda bidhaa au huduma mpya ya benki.",
    "products.updateProduct": "Sasisha bidhaa au huduma ya benki.",
    "products.name": "Jina la Bidhaa",
    "products.description": "Maelezo ya Bidhaa",
    "products.image": "Picha ya Bidhaa",
    "products.link": "Kiungo cha Bidhaa",
    "products.loading": "Inapakia bidhaa...",
    "products.deleted": "Bidhaa imefutwa kwa mafanikio.",
    "products.deleteError": "Kufuta bidhaa kumeshindwa",
    "products.saved": "Bidhaa imehifadhiwa kwa mafanikio.",
    "products.saveError": "Kuhifadhi bidhaa kumeshindwa",
    
    // News
    "news.title": "Habari na Matukio",
    "news.manage": "Simamia makala za habari na matukio",
    "news.addNew": "Ongeza Habari Mpya",
    "news.noNews": "Bado hakuna vipengele vya habari.",
    "news.createFirst": "Bofya \"Ongeza Habari Mpya\" ili uunde kipengele chako cha kwanza cha habari.",
    "news.addNewArticle": "Ongeza Makala Mpya ya Habari",
    "news.editArticle": "Hariri Makala ya Habari",
    "news.createArticle": "Unda makala mpya ya habari au tukio.",
    "news.updateArticle": "Sasisha makala ya habari au tukio.",
    "news.titleLabel": "Kichwa",
    "news.content": "Maudhui",
    "news.image": "Picha ya Kipekee",
    "news.category": "Jamii",
    "news.published": "Imechapishwa",
    "news.loading": "Inapakia makala ya habari...",
    "news.deleted": "Makala ya habari imefutwa kwa mafanikio.",
    "news.deleteError": "Kufuta makala ya habari kumeshindwa",
    "news.saved": "Makala ya habari imehifadhiwa kwa mafanikio.",
    "news.saveError": "Kuhifadhi makala ya habari kumeshindwa",
    
    // Investors
    "investors.title": "Habari za Wawekezaji",
    "investors.manage": "Simamia habari zinazohusiana na wawekezaji",
    "investors.categories": "Jamii za Wawekezaji",
    "investors.manageCategories": "Simamia jamii na hati za wawekezaji",
    "investors.addNew": "Ongeza Habari Mpya za Wawekezaji",
    "investors.editNews": "Hariri Habari za Wawekezaji",
    "investors.createNews": "Unda makala mpya ya habari za wawekezaji.",
    "investors.updateNews": "Sasisha makala ya habari za wawekezaji.",
    "investors.noNews": "Bado hakuna habari za wawekezaji.",
    "investors.createFirst": "Bofya \"Ongeza Habari Mpya za Wawekezaji\" ili uunde makala yako ya kwanza.",
    "investors.loading": "Inapakia habari za wawekezaji...",
    "investors.deleted": "Habari za wawekezaji zimefutwa kwa mafanikio.",
    "investors.deleteError": "Kufuta habari za wawekezaji kumeshindwa",
    "investors.saved": "Habari za wawekezaji zimehifadhiwa kwa mafanikio.",
    "investors.saveError": "Kuhifadhi habari za wawekezaji kumeshindwa",
    "investors.categoryName": "Jina la Jamii",
    "investors.addCategory": "Ongeza Jamii ya Wawekezaji",
    "investors.editCategory": "Hariri Jamii ya Wawekezaji",
    "investors.reportType": "Aina ya Ripoti",
    "investors.financialReport": "Ripoti ya Kifedha",
    "investors.annualReport": "Ripoti ya Mwaka",
    "investors.marketDisclosure": "Ufunuo wa Soko",
    "investors.rightsIssue": "Toleo la Haki",
    
    // Opportunities
    "opportunities.title": "Fursa",
    "opportunities.manage": "Simamia fursa za kazi na nafasi",
    "opportunities.addNew": "Ongeza Fursa Mpya",
    "opportunities.addNewOpportunity": "Ongeza Fursa Mpya",
    "opportunities.editOpportunity": "Hariri Fursa",
    "opportunities.titleLabel": "Kichwa",
    "opportunities.description": "Maelezo",
    "opportunities.requirements": "Mahitaji",
    "opportunities.deadline": "Mwisho wa Maombi",
    "opportunities.loading": "Inapakia fursa...",
    "opportunities.deleted": "Fursa imefutwa kwa mafanikio.",
    "opportunities.deleteError": "Kufuta fursa kumeshindwa",
    "opportunities.saved": "Fursa imehifadhiwa kwa mafanikio.",
    "opportunities.saveError": "Kuhifadhi fursa kumeshindwa",
    
    // Team
    "team.boardMembers": "Bodi ya Wakuu",
    "team.manageBoard": "Simamia wajumbe wa bodi",
    "team.management": "Usimamizi",
    "team.manageManagement": "Simamia wajumbe wa timu ya usimamizi",
    "team.members": "Wajumbe wa Timu",
    "team.manageMembers": "Simamia wajumbe wa timu",
    "team.addBoardMember": "Ongeza Mjumbe wa Bodi",
    "team.editBoardMember": "Hariri Mjumbe wa Bodi",
    "team.addManagement": "Ongeza Mjumbe wa Usimamizi",
    "team.editManagement": "Hariri Mjumbe wa Usimamizi",
    "team.name": "Jina",
    "team.position": "Nafasi",
    "team.bio": "Wasifu",
    "team.photo": "Picha",
    "team.loading": "Inapakia mjumbe...",
    "team.deleted": "Mjumbe amefutwa kwa mafanikio.",
    "team.deleteError": "Kufuta mjumbe kumeshindwa",
    "team.saved": "Mjumbe amehifadhiwa kwa mafanikio.",
    "team.saveError": "Kuhifadhi mjumbe kumeshindwa",
    "team.noMembers": "Bado hakuna wajumbe.",
    "team.createFirst": "Bofya \"Ongeza Mpya\" ili uunde mjumbe wako wa kwanza.",
    
    // Menu
    "menu.items": "Vipengele vya Menyu",
    "menu.manageItems": "Simamia vipengele vya urambazaji vya menyu",
    "menu.categories": "Jamii za Menyu",
    "menu.manageCategories": "Simamia jamii za menyu",
    "menu.addItem": "Ongeza Kipengele cha Menyu",
    "menu.editItem": "Hariri Kipengele cha Menyu",
    "menu.addCategory": "Ongeza Jamii ya Menyu",
    "menu.editCategory": "Hariri Jamii ya Menyu",
    "menu.itemName": "Jina la Kipengele",
    "menu.itemLink": "Kiungo cha Kipengele",
    "menu.categoryName": "Jina la Jamii",
    "menu.loading": "Inapakia kipengele cha menyu...",
    "menu.deleted": "Kipengele cha menyu kimefutwa kwa mafanikio.",
    "menu.deleteError": "Kufuta kipengele cha menyu kumeshindwa",
    "menu.saved": "Kipengele cha menyu kimehifadhiwa kwa mafanikio.",
    "menu.saveError": "Kuhifadhi kipengele cha menyu kumeshindwa",
    
    // Header
    "header.updates": "Matukio ya Kichwa",
    "header.manage": "Simamia matangazo ya kichwa",
    "header.addUpdate": "Ongeza Tukio la Kichwa",
    "header.editUpdate": "Hariri Tukio la Kichwa",
    "header.title": "Kichwa",
    "header.message": "Ujumbe",
    "header.isActive": "Inaendelea",
    "header.loading": "Inapakia tukio la kichwa...",
    "header.deleted": "Tukio la kichwa limefutwa kwa mafanikio.",
    "header.deleteError": "Kufuta tukio la kichwa kumeshindwa",
    "header.saved": "Tukio la kichwa limehifadhiwa kwa mafanikio.",
    "header.saveError": "Kuhifadhi tukio la kichwa kumeshindwa",
    
    // Wakala
    "wakala.title": "Maeneo ya Wakala",
    "wakala.manage": "Simamia maeneo ya Wakala ya Mwalimu",
    "wakala.addNew": "Ongeza Wakala Mpya",
    "wakala.addNewLocation": "Ongeza Eneo Jipya la Wakala",
    "wakala.editLocation": "Hariri Eneo la Wakala",
    "wakala.createLocation": "Unda eneo jipya la Wakala.",
    "wakala.updateLocation": "Sasisha eneo la Wakala.",
    "wakala.locationName": "Jina la Eneo",
    "wakala.address": "Anwani",
    "wakala.phone": "Simu",
    "wakala.email": "Barua Pepe",
    "wakala.loading": "Inapakia eneo la Wakala...",
    "wakala.deleted": "Eneo la Wakala limefutwa kwa mafanikio.",
    "wakala.deleteError": "Kufuta eneo la Wakala kumeshindwa",
    "wakala.saved": "Eneo la Wakala limehifadhiwa kwa mafanikio.",
    "wakala.saveError": "Kuhifadhi eneo la Wakala kumeshindwa",
    
    // FAQ
    "faq.title": "Maswali Yanayoulizwa Mara kwa Mara",
    "faq.manage": "Simamia maswali yanayoulizwa mara kwa mara",
    "faq.addNew": "Ongeza FAQ Mpya",
    "faq.noFaqs": "Hakuna FAQ zilizopatikana. Unda FAQ yako ya kwanza kuanza.",
    "faq.addNewFaq": "Ongeza FAQ Mpya",
    "faq.editFaq": "Hariri FAQ",
    "faq.createFaq": "Unda swali linaloulizwa mara kwa mara jipya.",
    "faq.updateFaq": "Sasisha swali linaloulizwa mara kwa mara.",
    "faq.question": "Swali",
    "faq.answer": "Jibu",
    "faq.isActive": "Inaendelea",
    "faq.loading": "Inapakia FAQ...",
    "faq.deleted": "FAQ imefutwa kwa mafanikio.",
    "faq.deleteError": "Kufuta FAQ kumeshindwa",
    "faq.saved": "FAQ imehifadhiwa kwa mafanikio.",
    "faq.saveError": "Kuhifadhi FAQ kumeshindwa",
    
    // Users
    "users.title": "Watumiaji",
    "users.manage": "Simamia watumiaji wa dashibodi",
    "users.addNew": "Ongeza Mtumiaji Mpya",
    "users.addNewUser": "Ongeza Mtumiaji Mpya",
    "users.editUser": "Hariri Mtumiaji",
    "users.username": "Jina la Mtumiaji",
    "users.email": "Barua Pepe",
    "users.password": "Nenosiri",
    "users.role": "Jukumu",
    "users.loading": "Inapakia mtumiaji...",
    "users.deleted": "Mtumiaji amefutwa kwa mafanikio.",
    "users.deleteError": "Kufuta mtumiaji kumeshindwa",
    "users.saved": "Mtumiaji amehifadhiwa kwa mafanikio.",
    "users.saveError": "Kuhifadhi mtumiaji kumeshindwa",
    
    // Session
    "session.expired": "Kikao Kimeisha",
    "session.expiredDesc": "Kikao chako kimeisha. Tafadhali ingia tena.",
    "session.invalidToken": "Tokeni Batili",
    "session.tokenExpired": "Tokeni iliyotolewa tayari imeisha. Tafadhali ingia tena.",
    
    // Charts
    "charts.newsUpdates": "Habari na Matukio",
    "charts.investorNews": "Habari za Wawekezaji",
    "charts.opportunities": "Fursa",
    "charts.products": "Bidhaa",
    "charts.carousels": "Carousels",
    "charts.menuItems": "Vipengele vya Menyu",
    "charts.trends": "Mienendo",
    "charts.monthlyGrowth": "Ukuaji wa kila mwezi",
    "charts.newsUpdatesTrends": "Mienendo ya Habari na Matukio",
    "charts.newsUpdatesDesc": "Ukuaji wa kila mwezi wa maudhui ya habari na matukio.",
    "charts.investorNewsTrends": "Mienendo ya Habari za Wawekezaji",
    "charts.investorNewsDesc": "Ukuaji wa kila mwezi wa maudhui ya habari za wawekezaji.",
    "charts.opportunitiesTrends": "Mienendo ya Fursa",
    "charts.opportunitiesDesc": "Ukuaji wa kila mwezi wa fursa za kazi na orodha.",
    "charts.productsTrends": "Mienendo ya Bidhaa",
    "charts.productsDesc": "Ukuaji wa kila mwezi wa bidhaa na huduma za benki.",
    "charts.carouselsTrends": "Mienendo ya Vipengele vya Carousel",
    "charts.carouselsDesc": "Ukuaji wa kila mwezi wa vipengele vya bango la carousel.",
    "charts.menuItemsTrends": "Mienendo ya Vipengele vya Menyu",
    "charts.menuItemsDesc": "Ukuaji wa kila mwezi wa vipengele vya menyu vinavyobadilika.",
    
    // Metrics
    "metrics.thisMonth": "mwezi huu",
  },
}

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  // Get initial language from localStorage or default to English
  const getInitialLanguage = (): Language => {
    const savedLang = localStorage.getItem("dashboardLanguage") as Language
    if (savedLang && (savedLang === "en" || savedLang === "sw")) {
      return savedLang
    }
    return "en"
  }

  const [language, setLanguageState] = useState<Language>(getInitialLanguage)

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem("dashboardLanguage", language)
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
  }

  // Translation function
  const t = (key: string): string => {
    return translations[language][key] || translations.en[key] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

